import React, { useState, useEffect } from 'react';
import { COLORS } from '../App';
import FileUpload from './FileUpload';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';
function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('cmbs_token');
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.body);
  }
  return fetch(`${API_BASE}${path}`, { ...opts, headers }).then(r => r.json());
}

const PROPERTY_TYPES = ['Office', 'Multifamily', 'Retail', 'Industrial', 'Hospitality', 'Mixed-Use', 'Self-Storage'];

async function authDownload(url, filename) {
  const token = localStorage.getItem('cmbs_token');
  const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
  if (!res.ok) { alert('Download failed'); return; }
  const blob = await res.blob();
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename || 'download';
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(a.href);
}

export default function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [form, setForm] = useState({ property_type: 'Office', template_name: '' });

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    const res = await apiFetch('/templates');
    if (res.success) setTemplates(res.data);
  };

  const handleUpload = async (files) => {
    if (!form.template_name.trim()) { alert('Enter a template name first'); return; }
    setUploading(true);
    setScanResult(null);
    const fd = new FormData();
    fd.append('file', files[0]);
    fd.append('property_type', form.property_type);
    fd.append('template_name', form.template_name);
    const token = localStorage.getItem('cmbs_token');
    const res = await fetch(`${API_BASE}/templates`, {
      method: 'POST', body: fd, headers: { 'Authorization': `Bearer ${token}` },
    }).then(r => r.json());
    if (res.success) {
      setScanResult(res.data.scan_results);
      setForm({ ...form, template_name: '' });
      await loadTemplates();
    } else {
      alert(res.error || 'Upload failed');
    }
    setUploading(false);
  };

  const handleRescan = async (id) => {
    const res = await apiFetch(`/templates/${id}/scan`);
    if (res.success) {
      setScanResult(res.data.scan_results);
      await loadTemplates();
    }
  };

  const toggleActive = async (id, pt) => {
    await apiFetch(`/templates/${id}/activate`, { method: 'PUT' });
    await loadTemplates();
  };

  const deactivate = async (id) => {
    await apiFetch(`/templates/${id}/deactivate`, { method: 'PUT' });
    await loadTemplates();
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template ${t.template_name} v${t.version}? This cannot be undone.`)) return;
    const res = await apiFetch(`/templates/${t.id}`, { method: 'DELETE' });
    if (res.success) { await loadTemplates(); }
    else { alert(res.error || 'Failed to delete'); }
  };

  const card = { background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 24, marginBottom: 20 };
  const inputStyle = { padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 14 };
  const labelStyle = { display: 'block', fontSize: 11, color: COLORS.textLight, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' };

  return (
    <div>
      <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 8 }}>UW Model Templates</h3>
      <p style={{ color: COLORS.textLight, fontSize: 13, marginBottom: 20 }}>
        Upload the firm's proprietary blank underwriting models. The system auto-scans each template to detect input cells, formula cells, and field mappings. No code changes needed when the model changes.
      </p>

      {/* Upload section */}
      <div style={card}>
        <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 12 }}>Upload New Template</h4>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Property Type</label>
            <select value={form.property_type} onChange={e => setForm({ ...form, property_type: e.target.value })} style={inputStyle}>
              {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Template Name</label>
            <input value={form.template_name} onChange={e => setForm({ ...form, template_name: e.target.value })}
              style={{ ...inputStyle, width: '100%' }} placeholder="e.g. Office UW Model v3.2" />
          </div>
        </div>
        <FileUpload onFiles={handleUpload} accept=".xlsx,.xls" label={uploading ? 'Uploading and scanning...' : 'Drop blank UW model template (.xlsx)'} multiple={false} />
      </div>

      {/* Scan results */}
      {scanResult && (
        <div style={{ ...card, borderLeft: `4px solid ${COLORS.gold}` }}>
          <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 12 }}>Template Scan Results</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
            <StatBox label="Input Cells" value={scanResult.total_input_cells} color="#2E7D32" />
            <StatBox label="Formula Cells" value={scanResult.total_formula_cells} color="#1565C0" />
            <StatBox label="Tabs" value={scanResult.tabs?.length || 0} color={COLORS.navy} />
            <StatBox label="Field Map Entries" value={scanResult.field_map_entries} color={COLORS.gold} />
          </div>

          {/* Cells by tab */}
          {scanResult.input_cells_by_tab && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...labelStyle, marginBottom: 8 }}>Input Cells by Tab</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(scanResult.input_cells_by_tab).map(([tab, count]) => (
                  <span key={tab} style={{ padding: '4px 12px', background: '#F0F2F5', borderRadius: 12, fontSize: 12, color: COLORS.navy }}>
                    {tab}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Diff from previous */}
          {scanResult.changes_from_previous && (
            <div style={{ padding: 12, background: '#FFF8E1', borderRadius: 6, fontSize: 12 }}>
              <strong style={{ color: '#F57F17' }}>Changes from previous version:</strong>
              <div style={{ marginTop: 6 }}>
                {scanResult.changes_from_previous.added_input_cells?.length > 0 && (
                  <div style={{ color: '#2E7D32' }}>+ {scanResult.changes_from_previous.added_input_cells.length} new input cells</div>
                )}
                {scanResult.changes_from_previous.removed_input_cells?.length > 0 && (
                  <div style={{ color: '#C62828' }}>- {scanResult.changes_from_previous.removed_input_cells.length} removed input cells</div>
                )}
                {!scanResult.changes_from_previous.added_input_cells?.length && !scanResult.changes_from_previous.removed_input_cells?.length && (
                  <div style={{ color: COLORS.textLight }}>No structural changes detected</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Existing templates */}
      {templates.length > 0 && (
        <div style={card}>
          <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 12 }}>Existing Templates</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                {['Property Type', 'Name', 'Ver', 'Input Cells', 'Formula Cells', 'Status', 'Uploaded', 'Actions'].map(h =>
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {templates.map(t => {
                const schema = t.field_schema || {};
                const inputCount = schema.input_cells ? Object.keys(schema.input_cells).length : 0;
                const formulaCount = schema.formula_cells ? schema.formula_cells.length : 0;
                return (
                  <tr key={t.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '10px', fontSize: 13, fontWeight: 500 }}>{t.property_type}</td>
                    <td style={{ padding: '10px', fontSize: 13 }}>{t.template_name}</td>
                    <td style={{ padding: '10px', fontSize: 13 }}>v{t.version}</td>
                    <td style={{ padding: '10px', fontSize: 13, color: '#2E7D32', fontWeight: 600 }}>{inputCount}</td>
                    <td style={{ padding: '10px', fontSize: 13, color: '#1565C0', fontWeight: 600 }}>{formulaCount}</td>
                    <td style={{ padding: '10px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        background: t.is_active ? '#E8F5E9' : '#F5F5F5', color: t.is_active ? '#2E7D32' : '#999' }}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '10px', fontSize: 12, color: COLORS.textLight }}>{new Date(t.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '10px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => handleRescan(t.id)}
                          style={{ padding: '3px 8px', background: '#F0F2F5', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>
                          Scan
                        </button>
                        <button onClick={() => authDownload(`${API_BASE}/templates/${t.id}/download`, `${t.template_name}_v${t.version}.xlsx`)}
                          style={{ padding: '3px 8px', background: '#F0F2F5', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 10, cursor: 'pointer', color: COLORS.text }}>
                          DL
                        </button>
                        {t.is_active ? (
                          <button onClick={() => deactivate(t.id)}
                            style={{ padding: '3px 8px', background: '#FFF', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 10, cursor: 'pointer', color: '#999' }}>
                            Off
                          </button>
                        ) : (
                          <>
                            <button onClick={() => toggleActive(t.id, t.property_type)}
                              style={{ padding: '3px 8px', background: COLORS.success, border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer', color: '#fff' }}>
                              On
                            </button>
                            <button onClick={() => handleDelete(t)}
                              style={{ padding: '3px 8px', background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 4, fontSize: 10, cursor: 'pointer', color: '#C62828' }}>
                              Del
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: 14, background: '#FAFBFC', borderRadius: 6, border: '1px solid #E9ECEF' }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || '#1A2332' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>{label}</div>
    </div>
  );
}
